import api from 'api/index';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { captureErrorInSentry } from 'utils/sentry';
import {
  BONUS_ASSIGNMENTS_PAGE_LIMIT,
  BONUS_COMPONENTS_PAGE_LIMIT,
  REARCH_BONUS_COMPONENT_NAMES_QUERY_VARIABLES,
} from '../components/AllocateBonusModal/constants';
import { useReArchBonusEnabled } from '../components/AllocateBonusModal/hooks/useReArchBonusEnabled';
import { bonusQueryKeys } from '../data';
import { fetchAllPages, mapAssignmentsToRows } from './utils';

export const useBonuses = (userId: string | null) => {
  const isReArchEnabled = useReArchBonusEnabled();

  const assignmentsQuery = useQuery(
    [bonusQueryKeys.rearchBonusAssignments, userId],
    () =>
      fetchAllPages(
        async (offset, limit) => {
          const response = await api.bonusAssignment.queries.listBonusAssignments({
            input: { user_id: userId!, limit, offset },
          });
          return {
            items: response?.payrollListBonusAssignments?.data ?? [],
            totalCount: response?.payrollListBonusAssignments?.total_count ?? 0,
          };
        },
        BONUS_ASSIGNMENTS_PAGE_LIMIT,
        'bonus assignments',
      ),
    { enabled: !!userId && isReArchEnabled },
  );

  const componentsQuery = useQuery(
    bonusQueryKeys.rearchBonusComponentNames,
    () =>
      fetchAllPages(
        async (offset, limit) => {
          const response = await api.bonusAssignment.queries.listBonusComponents({
            input: { ...REARCH_BONUS_COMPONENT_NAMES_QUERY_VARIABLES, limit, offset },
          });
          return {
            items: response?.payrollListFlexibleBenefitsComponents?.data ?? [],
            totalCount: response?.payrollListFlexibleBenefitsComponents?.total_count ?? 0,
          };
        },
        BONUS_COMPONENTS_PAGE_LIMIT,
        'bonus component names',
      ),
    { enabled: !!userId && isReArchEnabled },
  );

  const componentNames = useMemo(
    () =>
      Object.fromEntries(
        (componentsQuery.data ?? []).map((component) => [component.id, component.name]),
      ),
    [componentsQuery.data],
  );

  const bonuses = useMemo(
    () => mapAssignmentsToRows(assignmentsQuery.data ?? [], componentNames),
    [assignmentsQuery.data, componentNames],
  );

  useEffect(() => {
    const assignments = assignmentsQuery.data;
    if (assignments && assignments.length > 0 && bonuses.length === 0) {
      captureErrorInSentry(
        `Bonus table rendered empty despite ${assignments.length} assignment(s) loaded: ` +
          `every assignment came back with no live disbursements.`,
      );
    }
  }, [assignmentsQuery.data, bonuses.length]);

  return { ...assignmentsQuery, bonuses };
};
